// ink-component
import React, { ReactNode, ChangeEvent, FocusEvent, useContext, useEffect, useRef } from 'react';

import Control from '../Control/Control';
import Icon from '../Icon/Icon';

import useInput from '../../hooks/useInput';

import { bemHelper } from '../../helpers';
import setFocus from '../../helpers/dom/setFocus';
import getValue from './helpers/getValue';
import TableContext from '../Table/TableContext/TableContext';

import getGlobalProps from '../../helpers/constants/global-props';
import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';

type Meta = {
  initial?: boolean;
};

type Input = {
  onChange?: (event: Event) => void;
  onBlur?: (event: Event) => void;
  onFocus?: (event: Event) => void;
  value?: boolean;
};

export interface CheckboxProps {
  /** Unique identifier */
  id: string;
  /** Unique name */
  name?: string;
  /** Size modifier */
  size?: 'full' | 'large' | 'half' | 'quarter' | 'auto';
  /** Right label */
  label?: ReactNode;
  /** onFocus callback. Passes the Event */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  /** onChange callback. Passes the Event */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** onChange callback. Passes the Event */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Modifier for inline checkboxes */
  isInline?: boolean;
  /** External control and initial value for the checkbox */
  value?: boolean;
  /** Modifier for invalid state */
  isInvalid?: any;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** HTML type */
  autoFocus?: boolean;
  /** Marks a field as private to keep trackers from logging user input */
  dataPrivate?: boolean;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  meta?: Meta;
  /** @ignore */
  input?: Input;
  /** @ignore */
  isCell?: boolean;
  /** @ignore */
  isChecked?: boolean;
  /** @ignore */
  isFieldless?: boolean;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @categories form
 * @deprecated This component is deprecated and replaced by [NewCheckbox](https://ink.carta.com/components/NewCheckbox/)
 * @status deprecated
 * @replacementComponent NewCheckbox
 * @dateOfDeletion September 1st, 2023
 */
function Checkbox(props: CheckboxProps) {
  useDeprecatedWarning({ componentName: 'Checkbox', substitute: 'NewCheckbox' });
  const { tableId } = useContext(TableContext);
  const [value, setValue] = useInput(getValue(props?.input?.value, props.value, props?.meta?.initial, props.isChecked));

  const handleChange = event => {
    if (props?.input?.onChange) props.input.onChange(event);
    if (props.onChange) props.onChange(event);

    setValue(event.target.checked);
  };

  const handleFocus = event => {
    if (props?.input?.onFocus) props.input.onFocus(event);
    if (props?.onFocus) props.onFocus(event);
  };

  const handleBlur = event => {
    if (props?.input?.onBlur) props.input.onBlur(event);
    if (props?.onBlur) props.onBlur(event);
  };

  const defineClassName = () => {
    const b = bemHelper();

    return b('ink-control--checkbox', {
      cell: props.isCell || Boolean(tableId),
      fieldless: props.isFieldless,
    });
  };

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (props.autoFocus) setFocus(inputRef);
  }, [inputRef, props.autoFocus]);

  return (
    <Control
      inputId={props.id}
      size={props.size}
      isCell={props.isCell || Boolean(tableId)}
      isInline={props.isInline}
      isInvalid={props.isInvalid}
      isDisabled={props.isDisabled}
      isCheckbox
      dataPrivate={props.dataPrivate}
      data-testid={props['data-testid']}
    >
      <div className={defineClassName()} {...globalProps}>
        <input
          id={props.id}
          ref={inputRef}
          name={props.name}
          aria-label={props['aria-label']}
          type="checkbox"
          className="ink-control--checkbox--entry"
          checked={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={props.isDisabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={props.autoFocus}
          aria-checked={value}
        />
        <label className="ink-control--checkbox--appearance" htmlFor={props.id}>
          <div className="ink-control--checkbox--icon">
            <Icon type="checkmark" placement="low" />
          </div>
        </label>
        {Boolean(props.label) && (
          <label className="ink-control--checkbox--label" htmlFor={props.id}>
            {props.label}
          </label>
        )}
      </div>
    </Control>
  );
}

Checkbox.defaultProps = {
  name: undefined,
  size: 'large',
  label: undefined,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {},
  isInline: false,
  value: undefined,
  isInvalid: false,
  isDisabled: false,
  autoFocus: false,

  isChecked: undefined,
  meta: {},
  input: {
    onFocus: () => {},
    onChange: () => {},
    onBlur: () => {},
  },
  isCell: false,
  dataPrivate: undefined,
  'data-testid': undefined,
  isFieldless: undefined,
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
